import { CSSProperties, useState } from "react";

// material-ui
import { alpha, useTheme, Theme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

// third-party
import {
  Droppable,
  Draggable,
  DraggingStyle,
  NotDraggingStyle,
} from "@hello-pangea/dnd";

// project imports
import useConfig from "hooks/useConfig";
import EditColumn from "./EditColumn";
import Items from "./Items";
import AddItem from "./AddItem";
import AlertColumnDelete from "./AlertColumnDelete";
import { gridSpacing } from "store/constant";
import { openSnackbar } from "store/slices/snackbar";
import { useDispatch, useSelector } from "store";
import { deleteColumn } from "store/slices/kanban";

// assets
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";

// types
import { ThemeMode } from "types/config";
import { DefaultRootStateProps } from "types";
import { KanbanColumn } from "types/kanban";
import { KanbanItem } from "types/kanban";

interface Props {
  column: KanbanColumn;
  index: number;
}

// column drag wrapper
const getDragWrapper = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
  theme: Theme,
  radius: string
): CSSProperties | undefined => {
  const bgcolor =
    theme.palette.mode === ThemeMode.DARK
      ? theme.palette.background.default
      : theme.palette.primary.light;
  return {
    minWidth: 250,
    border: "1px solid",
    borderColor:
      theme.palette.mode === ThemeMode.DARK
        ? theme.palette.background.default
        : alpha(theme.palette.primary[200], 0.75),
    backgroundColor: isDragging ? theme.palette.grey[50] : bgcolor,
    borderRadius: radius,
    userSelect: "none",
    margin: `0 ${16}px 0 0`,
    height: "100%",
    ...draggableStyle,
  };
};

// column drop wrapper
const getDropWrapper = (
  isDraggingOver: boolean,
  theme: Theme,
  radius: string
) => {
  const bgcolor =
    theme.palette.mode === ThemeMode.DARK
      ? theme.palette.background.default
      : theme.palette.primary.light;
  const bgcolorDrop =
    theme.palette.mode === ThemeMode.DARK
      ? theme.palette.text.disabled
      : theme.palette.primary[200];

  return {
    background: isDraggingOver ? bgcolorDrop : bgcolor,
    padding: "8px 16px 14px",
    width: "auto",
    borderRadius: radius,
  };
};

// ==============================|| KANBAN BOARD - COLUMN ||============================== //

const Columns = ({ column, index }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { borderRadius } = useConfig();
  // const { items, columns, columnsOrder } = useSelector((state: DefaultRootStateProps) => state.kanban);

  let items: KanbanItem[] = [
    {
      assign: "profile-1",
      attachments: [],
      commentIds: ["comment-1"],
      description: "Content of item 1",
      dueDate: "Fri Feb 03 2023 15:20:14 GMT+0530 (India Standard Time)",
      id: "3542",
      image: "profile-back-1.png",
      priority: "low",
      title: "Online fees payment & instant announcements",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-2", "comment-5"],
      description: "Content of item 2",
      dueDate: "Sat Jan 28 2023 15:23:25 GMT+0530 (India Standard Time)",
      id: "4398",
      image: false,
      priority: "high",
      title: "Creation and Maintenance of Inventory Objects",
    },
    {
      assign: "profile-3",
      attachments: [],
      description: "Content of item 3",
      dueDate: "Tue Feb 07 2023 15:24:38 GMT+0530 (India Standard Time)",
      id: "6739",
      image: false,
      priority: "low",
      title: "Update React & TypeScript version",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-4"],
      description: "Content of item 4",
      dueDate: "Thu Feb 09 2023 15:25:46 GMT+0530 (India Standard Time)",
      id: "3950",
      image: "profile-back-2.png",
      priority: "low",
      title: "Set allowing rules for trusted applications.",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-1", "comment-2", "comment-5"],
      description: "Content of item 5",
      dueDate: "Mon Feb 06 2023 15:26:53 GMT+0530 (India Standard Time)",
      id: "6924",
      image: "profile-back-3.png",
      priority: "medium",
      title: "Managing Applications Launch Control",
    },
    {
      assign: "profile-3",
      attachments: [],
      commentIds: ["comment-3", "comment-4"],
      description: "Content of item 6",
      dueDate: "Wed Feb 15 2023 10:30:37 GMT+0530 (India Standard Time)",
      id: "9589",
      image: false,
      priority: "medium",
      title: "Run codemods",
    },
    {
      assign: "profile-1",
      attachments: [],
      description: "Content of item 7",
      dueDate: "Mon Feb 20 2023 15:29:58 GMT+0530 (India Standard Time)",
      id: "3410",
      image: "profile-back-4.png",
      priority: "low",
      title: "Purchase Requisitions, Adjustments, and Transfers.",
    },
    {
      assign: "profile-1",
      attachments: [],
      description: "Content of item 8",
      dueDate: "Sat Mar 04 2023 15:31:05 GMT+0530 (India Standard Time)",
      id: "8903",
      image: false,
      priority: "low",
      title: "Attendance checking & homework details",
    },
    {
      assign: "profile-3",
      attachments: [],
      commentIds: ["comment-3"],
      description: "Content of item 9",
      dueDate: "Thu Feb 23 2023 15:32:13 GMT+0530 (India Standard Time)",
      id: "1381",
      image: false,
      priority: "high",
      title: "Admission, Staff & Schedule management",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-5"],
      description: "Content of item 10",
      dueDate: "Mon Feb 27 2023 15:33:04 GMT+0530 (India Standard Time)",
      id: "8679",
      image: false,
      priority: "low",
      title: "Handling breaking changes",
    },
  ];
  
  let columns = [
    {
      id: "column-1",
      title: "New",
      itemIds: ["3542", "8903", "1381"],
    },
    {
      id: "column-2",
      title: "Active",
      itemIds: ["6739", "3950", "6924"],
    },
    {
      id: "column-3",
      title: "Resolved",
      itemIds: ["4398"],
    },
    {
      id: "column-4",
      title: "Closed",
      itemIds: ["9589", "3410", "8679"],
    },
  ];

  let columnsOrder = ["column-1", "column-2", "column-3", "column-4"];

  const columnItems = column.itemIds.map(
    (itemId) => items.filter((item) => item.id === itemId)[0]
  );

  const handleColumnDelete = () => {
    setOpen(true);
  };

  const [open, setOpen] = useState(false);
  const handleClose = (status: boolean) => {
    setOpen(false);
    if (status) {
      dispatch(deleteColumn(column.id, columnsOrder, columns));
      dispatch(
        openSnackbar({
          open: true,
          message: "Column deleted successfully",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    }
  };

  return (
    <Draggable draggableId={column.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getDragWrapper(
            snapshot.isDragging,
            provided.draggableProps.style,
            theme,
            `${borderRadius}px`
          )}
        >
          <Droppable droppableId={column.id} type="item">
            {(providedDrop, snapshotDrop) => (
              <div
                ref={providedDrop.innerRef}
                {...providedDrop.droppableProps}
                style={getDropWrapper(
                  snapshotDrop.isDraggingOver,
                  theme,
                  `${borderRadius}px`
                )}
              >
                <Grid container alignItems="center" spacing={gridSpacing}>
                  <Grid item xs zeroMinWidth>
                    <EditColumn column={column} />
                  </Grid>
                  <Grid item sx={{ mb: 1.5 }}>
                    <IconButton
                      onClick={handleColumnDelete}
                      size="large"
                      aria-label="Delete Columns"
                    >
                      <DeleteTwoToneIcon
                        fontSize="small"
                        aria-controls="menu-simple-card"
                        aria-haspopup="true"
                      />
                    </IconButton>
                    {open && (
                      <AlertColumnDelete
                        title={column.title}
                        open={open}
                        handleClose={handleClose}
                      />
                    )}
                  </Grid>
                </Grid>
                {columnItems.map((item, i) => (
                  <Items key={i} item={item} index={i} />
                ))}
                {providedDrop.placeholder}
                <AddItem columnId={column.id} />
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default Columns;
